import { useState, useEffect, useRef } from "react";
import axios from "axios";

const API = "https://danielcantos.net/api";

function pickLatinFemaleVoice() {
  const voices = window.speechSynthesis.getVoices();
  const esVoices = voices.filter(v => v.lang && v.lang.toLowerCase().startsWith("es"));
  const latinLangs = ["es-mx","es-us","es-419","es-co","es-ar","es-cl","es-pe","es-ve","es-ec","es-gt","es-do"];
  const femaleHints = ["paulina","mónica","monica","sabina","lupe","esperanza","marisol","camila","valentina","isabela","maría","maria","carla","laura","victoria","female","mujer","google español"];

  let candidates = esVoices.filter(v => latinLangs.includes(v.lang.toLowerCase()));
  if (candidates.length === 0) candidates = esVoices;

  const female = candidates.find(v => femaleHints.some(hint => v.name.toLowerCase().includes(hint)));
  return female || candidates[0] || null;
}

export default function DiplomadoChat() {
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [sessionId, setSessionId] = useState(null);
  const [documents, setDocuments] = useState([]);
  const [activeTab, setActiveTab] = useState("chat");
  const [voiceEnabled, setVoiceEnabled] = useState(false);
  const [voicesReady, setVoicesReady] = useState(false);
  const [listening, setListening] = useState(false);
  const [micSupported, setMicSupported] = useState(true);
  const messagesEndRef = useRef(null);
  const recognitionRef = useRef(null);

  useEffect(() => {
    const saved = localStorage.getItem("diplomado_messages");
    const savedSession = localStorage.getItem("diplomado_session_id");
    if (saved) setMessages(JSON.parse(saved));
    if (savedSession) setSessionId(savedSession);
    loadDocuments();

    const loadVoices = () => {
      if (window.speechSynthesis.getVoices().length > 0) setVoicesReady(true);
    };
    loadVoices();
    window.speechSynthesis.onvoiceschanged = loadVoices;

    const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (!SpeechRecognition) {
      setMicSupported(false);
    } else {
      const recognition = new SpeechRecognition();
      recognition.lang = "es-MX";
      recognition.continuous = false;
      recognition.interimResults = true;

      recognition.onresult = (event) => {
        let transcript = "";
        for (let i = 0; i < event.results.length; i++) {
          transcript += event.results[i][0].transcript;
        }
        setInput(transcript);
      };
      recognition.onerror = () => {
        setListening(false);
      };
      recognition.onend = () => {
        setListening(false);
      };
      recognitionRef.current = recognition;
    }
  }, []);

  useEffect(() => {
    localStorage.setItem("diplomado_messages", JSON.stringify(messages));
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  const loadDocuments = async () => {
    try {
      const res = await axios.get(`${API}/diplomado/documents`, { params: { category: "diplomado1" } });
      setDocuments(res.data || []);
    } catch {}
  };

  const speak = (text) => {
    if (!("speechSynthesis" in window)) {
      alert("Tu navegador no soporta lectura en voz alta.");
      return;
    }
    window.speechSynthesis.cancel();
    const utterance = new SpeechSynthesisUtterance(text);
    const voice = pickLatinFemaleVoice();
    if (voice) utterance.voice = voice;
    utterance.lang = voice ? voice.lang : "es-MX";
    utterance.rate = 1;
    utterance.pitch = 1;
    window.speechSynthesis.speak(utterance);
  };

  const stopSpeaking = () => {
    window.speechSynthesis.cancel();
  };

  const toggleListening = () => {
    if (!recognitionRef.current) {
      alert("Tu navegador no soporta dictado por voz. Prueba con Chrome o Edge.");
      return;
    }
    if (listening) {
      recognitionRef.current.stop();
      setListening(false);
    } else {
      setInput("");
      recognitionRef.current.start();
      setListening(true);
    }
  };

  const newChat = () => {
    stopSpeaking();
    setMessages([]);
    setSessionId(null);
    localStorage.removeItem("diplomado_messages");
    localStorage.removeItem("diplomado_session_id");
  };

  const sendMessage = async () => {
    const text = input.trim();
    if (!text || loading) return;
    setMessages(prev => [...prev, { role: "user", content: text }]);
    setInput("");
    setLoading(true);
    try {
      const res = await axios.post(`${API}/diplomado/chat`, {
        message: text,
        session_id: sessionId,
        category: "diplomado1",
      });
      const { response, session_id } = res.data;
      if (!sessionId) {
        setSessionId(session_id);
        localStorage.setItem("diplomado_session_id", session_id);
      }
      setMessages(prev => [...prev, { role: "assistant", content: response }]);
      if (voiceEnabled) speak(response);
    } catch (err) {
      const errMsg = err.response?.data?.detail || "Error al conectar con el servidor.";
      setMessages(prev => [...prev, { role: "assistant", content: `❌ Error: ${errMsg}` }]);
    } finally {
      setLoading(false);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); sendMessage(); }
  };

  return (
    <div style={{display:"flex",flexDirection:"column",height:"calc(100vh - 60px)",fontFamily:"sans-serif",background:"#f8fafc"}}>
      <div style={{display:"flex",borderBottom:"1px solid #e2e8f0",background:"white",padding:"0 16px",alignItems:"center"}}>
        {["chat","docs"].map(tab => (
          <button key={tab} onClick={() => setActiveTab(tab)} style={{padding:"12px 18px",border:"none",background:"transparent",cursor:"pointer",fontWeight:activeTab===tab?700:400,borderBottom:activeTab===tab?"2px solid #b45309":"2px solid transparent",color:activeTab===tab?"#b45309":"#64748b"}}>
            {tab==="chat" ? "💬 Chat" : "📚 Módulos"}
          </button>
        ))}
        <button onClick={newChat} style={{marginLeft:"auto",padding:"8px 14px",border:"none",background:"#b45309",color:"white",cursor:"pointer",fontWeight:600,fontSize:"12px",borderRadius:"6px"}}>
          + Nueva conversación
        </button>
      </div>

      {activeTab==="chat" && (
        <div style={{flex:1,display:"flex",flexDirection:"column"}}>
          <div style={{flex:1,overflowY:"auto",padding:"20px"}}>
            {messages.length===0 && (
              <div style={{textAlign:"center",color:"#94a3b8",marginTop:"60px"}}>
                <div style={{fontSize:"40px",marginBottom:"12px"}}>🎓</div>
                <div style={{fontSize:"16px",fontWeight:600}}>Diplomado en Economía Política</div>
                <div style={{fontSize:"13px",marginTop:"6px"}}>Pregunta sobre el contenido de tus módulos, pide repasos o resúmenes.</div>
              </div>
            )}
            {messages.map((msg,i) => (
              <div key={i} style={{marginBottom:"16px",display:"flex",justifyContent:msg.role==="user"?"flex-end":"flex-start"}}>
                <div style={{maxWidth:"72%",background:msg.role==="user"?"#b45309":"white",color:msg.role==="user"?"white":"#1e293b",padding:"12px 16px",borderRadius:"12px",boxShadow:"0 1px 4px rgba(0,0,0,0.08)",fontSize:"14px",lineHeight:"1.6"}}>
                  <div style={{whiteSpace:"pre-wrap"}}>{msg.content}</div>
                  {msg.role==="assistant" && (
                    <button onClick={() => speak(msg.content)} title="Escuchar" style={{marginTop:"8px",background:"transparent",border:"none",cursor:"pointer",fontSize:"16px",padding:0}}>
                      🔊
                    </button>
                  )}
                </div>
              </div>
            ))}
            {loading && (
              <div style={{display:"flex",justifyContent:"flex-start",marginBottom:"16px"}}>
                <div style={{background:"white",padding:"12px 16px",borderRadius:"12px",boxShadow:"0 1px 4px rgba(0,0,0,0.08)",color:"#64748b",fontSize:"14px"}}>
                  🔍 Repasando tus módulos...
                </div>
              </div>
            )}
            <div ref={messagesEndRef}/>
          </div>

          <div style={{padding:"12px 16px",background:"white",borderTop:"1px solid #e2e8f0"}}>
            <div style={{display:"flex",alignItems:"center",marginBottom:"10px",gap:"10px",flexWrap:"wrap"}}>
              <label style={{display:"flex",alignItems:"center",gap:"8px",cursor:"pointer",fontSize:"13px",color:"#475569"}}>
                <div onClick={() => { const v = !voiceEnabled; setVoiceEnabled(v); if (!v) stopSpeaking(); }} style={{width:"40px",height:"22px",borderRadius:"11px",background:voiceEnabled?"#b45309":"#cbd5e1",position:"relative",transition:"background 0.2s",cursor:"pointer"}}>
                  <div style={{position:"absolute",top:"3px",left:voiceEnabled?"20px":"3px",width:"16px",height:"16px",borderRadius:"50%",background:"white",transition:"left 0.2s",boxShadow:"0 1px 3px rgba(0,0,0,0.3)"}}/>
                </div>
                <span>{voiceEnabled ? "🔊 Leer respuestas en voz alta" : "🔇 Leer respuestas desactivado"}</span>
              </label>
              {!voicesReady && <span style={{fontSize:"11px",color:"#94a3b8"}}>(cargando voces del navegador...)</span>}
              {!micSupported && <span style={{fontSize:"11px",color:"#dc2626"}}>Dictado por voz no soportado en este navegador — usa Chrome o Edge.</span>}
            </div>
            <div style={{display:"flex",gap:"8px"}}>
              <button onClick={toggleListening} title={listening ? "Detener" : "Hablar"} style={{padding:"0 16px",background:listening?"#dc2626":"#e2e8f0",color:listening?"white":"#475569",border:"none",borderRadius:"8px",cursor:"pointer",fontSize:"16px"}}>
                {listening ? "⏹️" : "🎤"}
              </button>
              <textarea value={input} onChange={e => setInput(e.target.value)} onKeyDown={handleKeyDown} placeholder={listening ? "Escuchando... habla ahora" : "Escribe o toca 🎤 para hablar... (Enter para enviar)"} rows={2} style={{flex:1,padding:"10px 14px",border:listening ? "1px solid #dc2626" : "1px solid #e2e8f0",borderRadius:"8px",resize:"none",fontSize:"14px",outline:"none",fontFamily:"inherit"}}/>
              <button onClick={sendMessage} disabled={loading||!input.trim()} style={{padding:"0 20px",background:loading?"#94a3b8":"#b45309",color:"white",border:"none",borderRadius:"8px",cursor:loading?"not-allowed":"pointer",fontWeight:600,fontSize:"14px"}}>
                {loading?"...":"Enviar"}
              </button>
            </div>
          </div>
        </div>
      )}

      {activeTab==="docs" && (
        <div style={{padding:"20px",overflowY:"auto",flex:1}}>
          <h3 style={{margin:"0 0 16px",color:"#1e293b"}}>📚 Material indexado por módulo</h3>
          <div style={{fontSize:"12px",color:"#94a3b8",marginBottom:"16px"}}>
            Para subir material nuevo, usa el flujo de SCP + indexación en el servidor (aún no hay carga directa desde aquí).
          </div>
          {documents.length===0 ? (
            <div style={{textAlign:"center",color:"#94a3b8",marginTop:"40px"}}>No hay documentos indexados aún.</div>
          ) : (
            [1,2,3,4,5].map(modulo => {
              const docsModulo = documents.filter(d => d.modulo === modulo);
              if (docsModulo.length === 0) return null;
              return (
                <div key={modulo} style={{marginBottom:"20px"}}>
                  <div style={{fontWeight:700,color:"#b45309",fontSize:"14px",marginBottom:"8px"}}>Módulo {modulo}</div>
                  {docsModulo.map((doc,i) => (
                    <div key={i} style={{background:"white",borderRadius:"10px",padding:"14px 16px",boxShadow:"0 1px 4px rgba(0,0,0,0.06)",marginBottom:"8px"}}>
                      <div style={{fontWeight:600,color:"#1e293b",fontSize:"14px"}}>📄 {doc.filename}</div>
                      <div style={{fontSize:"12px",color:"#64748b",marginTop:"3px"}}>{doc.total_chunks} fragmentos · {doc.indexed_at ? new Date(doc.indexed_at).toLocaleDateString("es-EC") : ""}</div>
                    </div>
                  ))}
                </div>
              );
            })
          )}
        </div>
      )}
    </div>
  );
}
